/**
 * UI Store
 *
 * Manages UI state (modals, sidebars, etc.)
 */

import { create } from 'zustand';

export interface UIState {
  isModalOpen: boolean;
  isSidebarOpen: boolean;
  isLoading: boolean;
  notification?: {
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
  };
  customUI?: Record<string, unknown>;
}

export interface UIStore {
  ui: UIState;
  openModal: () => void;
  closeModal: () => void;
  toggleModal: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  setLoading: (isLoading: boolean) => void;
  setNotification: (notification: UIState['notification']) => void;
  clearNotification: () => void;
  setCustomUI: (key: string, value: unknown) => void;
}

const defaultUIState: UIState = {
  isModalOpen: false,
  isSidebarOpen: true,
  isLoading: false,
};

export const useUIStore = create<UIStore>((set) => ({
  ui: defaultUIState,

  openModal: () => set((state) => ({ ui: { ...state.ui, isModalOpen: true } })),

  closeModal: () => set((state) => ({ ui: { ...state.ui, isModalOpen: false } })),

  toggleModal: () =>
    set((state) => ({
      ui: { ...state.ui, isModalOpen: !state.ui.isModalOpen },
    })),

  openSidebar: () => set((state) => ({ ui: { ...state.ui, isSidebarOpen: true } })),

  closeSidebar: () => set((state) => ({ ui: { ...state.ui, isSidebarOpen: false } })),

  toggleSidebar: () =>
    set((state) => ({
      ui: { ...state.ui, isSidebarOpen: !state.ui.isSidebarOpen },
    })),

  setLoading: (isLoading: boolean) =>
    set((state) => ({ ui: { ...state.ui, isLoading } })),

  setNotification: (notification: UIState['notification']) =>
    set((state) => ({ ui: { ...state.ui, notification } })),

  clearNotification: () =>
    set((state) => ({ ui: { ...state.ui, notification: undefined } })),

  setCustomUI: (key: string, value: unknown) =>
    set((state) => ({
      ui: {
        ...state.ui,
        customUI: { ...state.ui.customUI, [key]: value },
      },
    })),
}));
